import { useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { 
  Bold, Italic, Underline, List, ListOrdered, 
  Heading1, Heading2, Heading3, Link2, Image, Upload,
  AlignLeft, AlignCenter, Quote, Undo, Redo,
  RemoveFormatting, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extracted toolbar components to avoid re-creation on each render
const ToolbarButton = ({ onClick, title, children, active = false }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    title={title}
    className={`h-8 w-8 p-0 ${active ? 'bg-stone-200' : ''}`}
  >
    {children}
  </Button>
);

const ToolbarDivider = () => (
  <div className="w-px h-6 bg-stone-200 mx-1" />
);

/**
 * Simple Rich Text Editor using contenteditable
 * Works reliably without external dependencies
 * Supports i18n for toolbar labels
 */
export default function RichTextEditor({ value, onChange, placeholder }) {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInternalChange = useRef(false);
  const [isUploading, setIsUploading] = useState(false);

  // DOMPurify config for initialization - preserve all formatting tags
  const INIT_PURIFY_CONFIG = {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel'],
    KEEP_CONTENT: true
  };

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      // SEC-FIX: Sanitize HTML before assignment to prevent XSS, but preserve formatting
      const sanitizedValue = DOMPurify.sanitize(value || '', INIT_PURIFY_CONFIG);
      if (editorRef.current.innerHTML !== sanitizedValue) {
        editorRef.current.innerHTML = sanitizedValue;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Execute formatting command
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  // Format as heading
  const formatHeading = (level) => {
    execCommand('formatBlock', `h${level}`);
  };

  // Format as paragraph
  const formatParagraph = () => {
    execCommand('formatBlock', 'p');
  };

  // Insert link
  const insertLink = () => {
    const url = prompt('Syötä linkin URL:', 'https://');
    if (url) {
      execCommand('createLink', url);
    }
  };

  // Insert image
  const insertImage = () => {
    const url = prompt('Syötä kuvan URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  // Handle file upload for images
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('editor.invalidFileType', 'Invalid file type. Allowed: JPEG, PNG, WebP, GIF'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('editor.fileTooLarge', 'File too large. Maximum size is 5MB'));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      if (response.data?.url) {
        // Insert the uploaded image into the editor
        editorRef.current?.focus();
        execCommand('insertImage', response.data.url);
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      alert(t('editor.uploadFailed', 'Failed to upload image. Please try again.'));
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle drag over
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editorRef.current) {
      editorRef.current.classList.add('ring-2', 'ring-rose-400', 'ring-offset-2');
    }
  }, []);

  // Handle drag leave
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editorRef.current) {
      editorRef.current.classList.remove('ring-2', 'ring-rose-400', 'ring-offset-2');
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Remove drag highlight
    if (editorRef.current) {
      editorRef.current.classList.remove('ring-2', 'ring-rose-400', 'ring-offset-2');
    }

    // Get dropped files
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Process first image file
    const file = Array.from(files).find(f => f.type.startsWith('image/'));
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('editor.invalidFileType', 'Invalid file type. Allowed: JPEG, PNG, WebP, GIF'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('editor.fileTooLarge', 'File too large. Maximum size is 5MB'));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      if (response.data?.url) {
        // Focus editor and insert the uploaded image
        editorRef.current?.focus();
        execCommand('insertImage', response.data.url);
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      alert(t('editor.uploadFailed', 'Failed to upload image. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  }, [t]);

  // DOMPurify config - allow structural HTML tags for proper formatting
  const DOMPURIFY_CONFIG = {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel'],
    KEEP_CONTENT: true
  };

  // Handle paste - clean up and preserve basic formatting, support image paste
  const handlePaste = useCallback(async (e) => {
    // Check for image files in clipboard (screenshots, copied images)
    const items = e.clipboardData?.items;
    if (items) {
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          
          const file = item.getAsFile();
          if (!file) continue;
          
          // Validate file type
          const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
          if (!allowedTypes.includes(file.type)) {
            alert(t('editor.invalidFileType', 'Invalid file type. Allowed: JPEG, PNG, WebP, GIF'));
            return;
          }
          
          // Validate file size (5MB max)
          if (file.size > 5 * 1024 * 1024) {
            alert(t('editor.fileTooLarge', 'File too large. Maximum size is 5MB'));
            return;
          }
          
          setIsUploading(true);
          
          try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              withCredentials: true,
            });
            
            if (response.data?.url) {
              editorRef.current?.focus();
              execCommand('insertImage', response.data.url);
            }
          } catch (error) {
            console.error('Image paste upload failed:', error);
            alert(t('editor.uploadFailed', 'Failed to upload image. Please try again.'));
          } finally {
            setIsUploading(false);
          }
          
          return; // Exit after handling image
        }
      }
    }
    
    // Not an image - handle text paste
    e.preventDefault();
    
    // Get both HTML and plain text
    const htmlContent = e.clipboardData.getData('text/html');
    const plainText = e.clipboardData.getData('text/plain');
    
    let content;
    
    if (htmlContent && htmlContent.trim()) {
      // Process HTML content (from Word, Notion, web pages, etc.)
      content = processHtmlPaste(htmlContent);
    } else if (plainText && plainText.trim()) {
      // Process plain text (convert line breaks to paragraphs)
      content = processPlainTextPaste(plainText);
    } else {
      return; // Nothing to paste
    }
    
    // SEC-FIX: Final sanitization with allowed tags config
    const sanitizedContent = DOMPurify.sanitize(content, DOMPURIFY_CONFIG);
    
    // Insert the cleaned content
    document.execCommand('insertHTML', false, sanitizedContent);
    handleInput();
  }, [handleInput, t]);

  // Process HTML paste from various sources
  const processHtmlPaste = (html) => {
    // Create a temporary container to parse HTML
    const container = document.createElement('div');
    container.innerHTML = html;
    
    // Remove Word-specific elements and styles
    container.querySelectorAll('meta, link, style, script, xml, o\\:p').forEach(el => el.remove());
    
    // Process the content to extract clean HTML
    const processNode = (node, parentIsList = false) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        // Preserve text content
        return text;
      }
      
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }
      
      const tagName = node.tagName.toLowerCase();
      let children = Array.from(node.childNodes).map(child => processNode(child, tagName === 'ul' || tagName === 'ol')).join('');
      
      // Map elements to clean HTML
      switch (tagName) {
        // Headings
        case 'h1': return `<h1>${children}</h1>`;
        case 'h2': return `<h2>${children}</h2>`;
        case 'h3': return `<h3>${children}</h3>`;
        case 'h4': return `<h4>${children}</h4>`;
        case 'h5': return `<h5>${children}</h5>`;
        case 'h6': return `<h6>${children}</h6>`;
        
        // Paragraphs and divs become paragraphs
        case 'p':
          return children.trim() ? `<p>${children}</p>` : '';
        case 'div':
          // Notion uses divs - convert to paragraphs if they contain text
          if (children.trim() && !children.includes('<p>') && !children.includes('<h')) {
            return `<p>${children}</p>`;
          }
          return children;
        
        // Lists
        case 'ul': return `<ul>${children}</ul>`;
        case 'ol': return `<ol>${children}</ol>`;
        case 'li': return `<li>${children}</li>`;
        
        // Inline formatting
        case 'strong':
        case 'b':
          return `<strong>${children}</strong>`;
        case 'em':
        case 'i':
          return `<em>${children}</em>`;
        case 'u':
          return `<u>${children}</u>`;
        
        // Links
        case 'a':
          const href = node.getAttribute('href');
          return href ? `<a href="${href}">${children}</a>` : children;
        
        // Images
        case 'img':
          const src = node.getAttribute('src');
          const alt = node.getAttribute('alt') || '';
          return src ? `<img src="${src}" alt="${alt}">` : '';
        
        // Blockquotes
        case 'blockquote':
          return `<blockquote>${children}</blockquote>`;
        
        // Line breaks
        case 'br':
          return '<br>';
        
        // Spans and other inline elements - just return children
        case 'span':
        case 'font':
          return children;
        
        // Ignore these
        case 'meta':
        case 'link':
        case 'style':
        case 'script':
          return '';
        
        // Default - return children
        default:
          return children;
      }
    };
    
    let result = processNode(container);
    
    // Clean up multiple line breaks
    result = result
      .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')
      .replace(/<p>\s*<\/p>/gi, '')
      .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '')
      .trim();
    
    // Ensure content is wrapped in block elements
    if (result && !result.match(/^<(p|h[1-6]|ul|ol|blockquote)/i)) {
      result = `<p>${result}</p>`;
    }
    
    return result;
  };

  // Process plain text paste - convert to proper HTML
  const processPlainTextPaste = (text) => {
    // Escape HTML entities first
    let escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Split by double newlines to get paragraphs
    const paragraphs = escaped.split(/\n\s*\n/);
    
    const processedParagraphs = paragraphs.map(para => {
      // Trim and skip empty paragraphs
      para = para.trim();
      if (!para) return '';
      
      // Check for markdown-style headings
      if (para.match(/^### /)) {
        return `<h3>${para.replace(/^### /, '')}</h3>`;
      }
      if (para.match(/^## /)) {
        return `<h2>${para.replace(/^## /, '')}</h2>`;
      }
      if (para.match(/^# /)) {
        return `<h1>${para.replace(/^# /, '')}</h1>`;
      }
      
      // Check for bullet lists (lines starting with - or *)
      const lines = para.split('\n');
      const isList = lines.every(line => line.trim().match(/^[-*•]\s/) || !line.trim());
      
      if (isList && lines.some(line => line.trim())) {
        const listItems = lines
          .filter(line => line.trim())
          .map(line => `<li>${line.trim().replace(/^[-*•]\s*/, '')}</li>`)
          .join('');
        return `<ul>${listItems}</ul>`;
      }
      
      // Check for numbered lists
      const isNumberedList = lines.every(line => line.trim().match(/^\d+[.)]\s/) || !line.trim());
      
      if (isNumberedList && lines.some(line => line.trim())) {
        const listItems = lines
          .filter(line => line.trim())
          .map(line => `<li>${line.trim().replace(/^\d+[.)]\s*/, '')}</li>`)
          .join('');
        return `<ol>${listItems}</ol>`;
      }
      
      // Process inline formatting (bold, italic)
      para = para
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/__(.+?)__/g, '<strong>$1</strong>')
        .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
        .replace(/_([^_]+?)_/g, '<em>$1</em>');
      
      // Convert single newlines to <br> within paragraphs
      para = para.replace(/\n/g, '<br>');
      
      return `<p>${para}</p>`;
    });
    
    return processedParagraphs.filter(p => p).join('');
  };

  // Handle Enter key for proper paragraph breaks and keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        case 'k':
          e.preventDefault();
          insertLink();
          break;
        case '1':
          e.preventDefault();
          formatHeading(1);
          break;
        case '2':
          e.preventDefault();
          formatHeading(2);
          break;
        case '3':
          e.preventDefault();
          formatHeading(3);
          break;
        default:
          // Let browser handle other shortcuts (Ctrl+Z, Ctrl+Y, etc.)
          break;
      }
      return;
    }
    
    // Handle Enter key for proper paragraph breaks
    if (e.key === 'Enter' && !e.shiftKey) {
      // Let default behavior handle it, but ensure we're in a block element
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const parentElement = range.startContainer.parentElement;
        
        // If we're directly in the editor div, wrap in paragraph
        if (parentElement === editorRef.current) {
          e.preventDefault();
          execCommand('insertParagraph');
        }
      }
    }
  }, [insertLink, formatHeading]);

  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden relative">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 bg-stone-50 border-b border-stone-200">
        {/* Undo/Redo */}
        <ToolbarButton onClick={() => execCommand('undo')} title={t('editor.undo', 'Undo (Ctrl+Z)')}>
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('redo')} title={t('editor.redo', 'Redo (Ctrl+Y)')}>
          <Redo className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Headings */}
        <ToolbarButton onClick={() => formatHeading(1)} title={t('editor.heading1', 'Heading 1')}>
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => formatHeading(2)} title={t('editor.heading2', 'Heading 2')}>
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => formatHeading(3)} title={t('editor.heading3', 'Heading 3')}>
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={formatParagraph} title={t('editor.paragraph', 'Normal text')}>
          <span className="text-xs font-medium">P</span>
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Basic formatting */}
        <ToolbarButton onClick={() => execCommand('bold')} title={t('editor.bold', 'Bold (Ctrl+B)')}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title={t('editor.italic', 'Italic (Ctrl+I)')}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('underline')} title={t('editor.underline', 'Underline (Ctrl+U)')}>
          <Underline className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Lists */}
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title={t('editor.bulletList', 'Bullet list')}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title={t('editor.numberedList', 'Numbered list')}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Block formatting */}
        <ToolbarButton onClick={() => execCommand('formatBlock', 'blockquote')} title={t('editor.quote', 'Quote')}>
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyLeft')} title={t('editor.alignLeft', 'Align left')}>
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyCenter')} title={t('editor.alignCenter', 'Center')}>
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Insert */}
        <ToolbarButton onClick={insertLink} title={t('editor.insertLink', 'Insert link')}>
          <Link2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={insertImage} title={t('editor.insertImage', 'Insert image URL')}>
          <Image className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton 
          onClick={() => fileInputRef.current?.click()} 
          title={t('editor.uploadImage', 'Upload image')}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
        </ToolbarButton>
        
        {/* Hidden file input for image upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileUpload}
          className="hidden"
        />
        
        <ToolbarDivider />
        
        {/* Clear formatting */}
        <ToolbarButton onClick={() => execCommand('removeFormat')} title={t('editor.removeFormatting', 'Remove formatting')}>
          <RemoveFormatting className="h-4 w-4" />
        </ToolbarButton>
      </div>
      
      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`min-h-[400px] p-4 prose prose-stone max-w-none focus:outline-none transition-all duration-200 ${isUploading ? 'opacity-50' : ''}`}
        style={{
          lineHeight: '1.7',
          fontSize: '16px'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
      
      {/* Drop zone overlay when uploading */}
      {isUploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 pointer-events-none">
          <div className="flex items-center gap-2 text-stone-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{t('editor.uploading', 'Uploading...')}</span>
          </div>
        </div>
      )}
      
      {/* Placeholder styles */}
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] p {
          margin-bottom: 1em;
        }
        [contenteditable] h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        [contenteditable] h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.75em 0;
        }
        [contenteditable] h3 {
          font-size: 1.25em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        [contenteditable] ul, [contenteditable] ol {
          padding-left: 2em;
          margin: 1em 0;
        }
        [contenteditable] li {
          margin: 0.5em 0;
        }
        [contenteditable] blockquote {
          border-left: 4px solid #e5e5e5;
          padding-left: 1em;
          margin: 1em 0;
          color: #6b7280;
          font-style: italic;
        }
        [contenteditable] a {
          color: #f43f5e;
          text-decoration: underline;
        }
        [contenteditable] img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1em 0;
        }
      `}</style>
    </div>
  );
}
